import type { Language, Locale, StaticSiteConfig } from "../../site-config";

export type LocalizedValue<T = string> = {
	[locale: string]: T;
};

const ctLocalesMap: { [key: string]: string } = {
	nl: "nl-NL",
	fr: "fr-FR",
	en: "en-GB",
	de: "de-DE",
	at: "de-DE",
	it: "it-IT",
	es: "es-ES",
};

export const ctLocales = Object.values(ctLocalesMap);
export const akeneoLocales = ctLocalesMap;
export const buckarooLocales = ctLocalesMap;

export const toCtLocale = (locale: string) => ctLocalesMap[languageOf(locale)];

export const toAkeneoLocale = (locale: string) =>
	akeneoLocales[languageOf(locale)];

export const isLocalizedObject = (obj: unknown): obj is LocalizedValue => {
	if (typeof obj !== "object" || obj === null) return false;
	const keys = Object.keys(obj);
	return (
		keys.length > 0 &&
		keys.every((key) => {
			const parts = key.split("-");
			if (parts[0].length !== 2) return false;
			if (parts[1] && parts[1].length !== 2) return false;

			const value = obj[key as keyof typeof obj];
			return typeof value === "string" || typeof value === "number";
		})
	);
};

export const getLocalizedStrict = <T = string>(
	values: Record<string, T>,
	locale: string,
): T | undefined =>
	[locale, languageOf(locale)].reduce(
		// return the first translated value that exists
		(p, l) => values[l] || values[ctLocalesMap[l]] || p,
		undefined as T | undefined,
	);

/**
 * Returns the localized value for the given locale(s).
 *
 * Tries the given locales in order, and also tries the language of the locale.
 */
export const getLocalizedValue = <T = string>(
	values: {
		[key: string]: T;
	},
	locale: string,
): T =>
	getLocalizedStrict(values, locale) ??
	// Temporarily fallback if no value exists
	Object.values(values)[0];

export const getLocalizedValueSafe = (
	value: string | LocalizedValue | undefined,
	language: Language,
) => {
	if (!value) return value;
	if (isLocalizedObject(value)) return getLocalizedValue(value, language);
	return value;
};

export const countryOf = <T extends string>(locale?: string) =>
	locale?.split("-")[1] as T;

export const languageOf = <T extends Language>(locale?: string) =>
	locale?.split("-")[0] as T;

/**
 * In the case of HBM, each country represents a market (and vice versa), so knowing the country is knowing the market
 */
export const toMarket = countryOf;

export const isFullLocale = (locale: string) => locale.includes("-");

export const flagPath = (country: string) =>
	`/assets/flags/${country.toLowerCase()}.svg`;

export const getDisplayName = (
	currentStaticSiteConfig: StaticSiteConfig,
	locale: string,
) => {
	const languageLabel = new Intl.DisplayNames([locale], {
		type: "language",
	}).of(languageOf(locale)) as string;

	if (
		currentStaticSiteConfig.country.toLowerCase() !==
		currentStaticSiteConfig.defaultLanguage
	) {
		const countryLabel = new Intl.DisplayNames([locale], {
			type: "region",
		}).of(countryOf(locale));

		return `${languageLabel} (${countryLabel})`;
	}
	return languageLabel;
};

/**
 * Get the country display name based on the locale and the country code
 *
 * @example
 * getCountryDisplayName("nl-NL", "DE") // Duitsland
 * getCountryDisplayName("de-AT", "DE") // Deutschland
 */
export const getCountryDisplayName = (
	locale?: Locale,
	countryCode?: string,
) => {
	if (!countryCode || !locale) return;
	try {
		return new Intl.DisplayNames([locale], { type: "region" }).of(countryCode);
	} catch (err) {
		return;
	}
};
